import pronunciationOverrides from './pronunciationOverrides.json'
import pronunciations from './pronunciations.json'

export interface SpeechToken {
  text: string;
  wordIndex: number | null;
}

export interface WordCollector {
  currentBlock: unknown;
  finish(): string[];
  setBlock(block: unknown): void;
  tokenize(text: string): SpeechToken[];
  words: string[];
}

export const WHITESPACE_PATTERN = new RegExp('[\\t\\n\\v\\f\\r \\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF]+')
const WORD_BOUNDARY_SPLIT_PATTERN = new RegExp(`(${WHITESPACE_PATTERN.source}|\u2014)`)
const WHITESPACE_ONLY_PATTERN = new RegExp(`^${WHITESPACE_PATTERN.source}$`)
const PUNCTUATION_ONLY_PATTERN = new RegExp('^[^\\p{L}\\p{N}_]+$', 'u')
const BLANK_PATTERN = /_{2,}/g
export const BLANK_WORD = 'blank'
const LEADING_PUNCTUATION_PATTERN = new RegExp('^[^\\p{L}\\p{N}_]+', 'u')
const TRAILING_PUNCTUATION_PATTERN = new RegExp('[^\\p{L}\\p{N}_]+$', 'u')
const TERMINAL_PUNCTUATION_PATTERN = new RegExp('[.!?:;\u2026][\\p{Pf}\\p{Pe}"\'\u2019\u201D)\\]]*$', 'u')
const SENTENCE_END = '.'

export const BLOCK_ELEMENTS = ['address', 'article', 'aside', 'blockquote', 'dd', 'details', 'dialog', 'div', 'dl', 'dt', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul']

export const endsSentence = (word: string) => TERMINAL_PUNCTUATION_PATTERN.test(word)

export const isPunctuationOnly = (token: string) => PUNCTUATION_ONLY_PATTERN.test(token)

type SpokenForms = Record<string, string>

const lowercaseKeys = (forms: SpokenForms): SpokenForms => Object.fromEntries(Object.entries(forms).map(([word, spoken]) => [word.toLowerCase(), spoken]))

const PRONUNCIATIONS: SpokenForms = lowercaseKeys(pronunciations)
const PRONUNCIATION_OVERRIDES: Record<string, SpokenForms> = Object.fromEntries(Object.entries(pronunciationOverrides as Record<string, SpokenForms>).map(([text, forms]) => [text, lowercaseKeys(forms)]))

const respelledFrom = (token: string, forms: SpokenForms) => {
  const whole = forms[token.toLowerCase()]
  if (whole) { return whole }

  const leading = token.match(LEADING_PUNCTUATION_PATTERN)?.[0] || ''
  const trailing = token.match(TRAILING_PUNCTUATION_PATTERN)?.[0] || ''
  const spelled = forms[token.slice(leading.length, token.length - trailing.length).toLowerCase()]

  return spelled ? `${leading}${spelled}${trailing}` : null
}

const respelled = (token: string) => respelledFrom(token, PRONUNCIATIONS)

export const spokenForm = (token: string) => respelled(token) || token.replace(BLANK_PATTERN, BLANK_WORD)

export const spokenPhrase = (text: string) => text.split(/(\s+)/).map(part => (part.trim() ? spokenForm(part) : part)).join('')

export const applyPronunciationOverrides = (text: string) => {
  const forms = PRONUNCIATION_OVERRIDES[text]
  if (!forms) { return text }

  return text.split(/(\s+)/).map(part => (part.trim() ? (respelledFrom(part, forms) || part) : part)).join('')
}

export const speechTextFromWords = (words: string[]) => applyPronunciationOverrides(words.join(' ').normalize('NFC'))

export function createWordCollector(): WordCollector {
  const words: string[] = []
  let previousTokenWasWord = false
  let currentBlock: unknown = null
  let blockStartWordCount = 0

  const closeBlock = () => {
    if (words.length > blockStartWordCount && !endsSentence(words[words.length - 1])) { words[words.length - 1] += SENTENCE_END }
    blockStartWordCount = words.length
  }

  const setBlock = (block: unknown) => {
    if (block === currentBlock) { return }
    closeBlock()
    currentBlock = block
  }

  const finish = () => {
    closeBlock()
    return words
  }

  const tokenize = (text: string): SpeechToken[] => {
    const tokens: SpeechToken[] = []
    text.split(WORD_BOUNDARY_SPLIT_PATTERN).forEach((part) => {
      if (!part) { return }

      if (WHITESPACE_ONLY_PATTERN.test(part)) {
        previousTokenWasWord = false
        tokens.push({ text: part, wordIndex: null, })
        return
      }

      let wordIndex: number
      if (previousTokenWasWord && isPunctuationOnly(part)) {
        wordIndex = words.length - 1
        words[wordIndex] += spokenForm(part)
      } else {
        wordIndex = words.length
        words.push(spokenForm(part))
      }
      previousTokenWasWord = true
      tokens.push({ text: part, wordIndex, })
    })
    return tokens
  }

  return { finish, get currentBlock() { return currentBlock }, setBlock, tokenize, words, }
}
